import React, { useContext, useEffect, useRef, useState } from 'react';
import { Button, Form, Input, Popconfirm, Table, Select, Modal, Space, Tooltip } from 'antd';
import { DeleteOutlined, LockOutlined} from '@ant-design/icons';
import { Trash2 } from 'lucide-react';
import AxiosCaller from '../../../authenticatie/AxiosCaller';
import { openNotificationSuccess, openNotificationError } from '../../../Components/Notification/Notification';


const EditableContext = React.createContext(null);
const EditableRow = ({ index, ...props }) => {
  const [form] = Form.useForm();
  return (
    <Form form={form} component={false}>
      <EditableContext.Provider value={form}>
        <tr {...props} />
      </EditableContext.Provider>
    </Form>
  );
};

const EditableCell = ({
  title,
  editable,
  children,
  dataIndex,
  record,
  handleSave,
  inputType,
  options,
  ...restProps
}) => {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef(null);
  const form = useContext(EditableContext);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
    }
  }, [editing]);

  const toggleEdit = () => {
    setEditing(!editing);
    form.setFieldsValue({ [dataIndex]: record[dataIndex] });
  };

  const save = async () => {
    try {
      const values = await form.validateFields();
      toggleEdit();
      handleSave({ ...record, ...values });
    } catch (errInfo) {
      console.error('Save failed:', errInfo);
    }
  };

  let childNode = children;

  if (editable) {
    childNode = editing ? (
      <Form.Item
        style={{ margin: 0 }}
        name={dataIndex}
        rules={[{ required: true, message: `${title} is required.` }]}
      >
        {inputType === 'select' ? (
          <Select ref={inputRef} onBlur={save} onChange={save}>
            {options.map((opt) => (
              <Select.Option key={opt} value={opt}>
                {opt}
              </Select.Option>
            ))}
          </Select>
        ) : (
          <Input ref={inputRef} onPressEnter={save} onBlur={save} />
        )}
      </Form.Item>
    ) : (
      <div className="editable-cell-value-wrap" style={{ paddingInlineEnd: 24 }} onClick={toggleEdit}>
        {children}
      </div>
    );
  }
  return <td {...restProps}>{childNode}</td>;
};

const GebruikersBeheerScreen = () => {
  const { get, put, del, post } = AxiosCaller();
  const [users, setUsers] = useState([]);
  const [rollen, setRollen] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [userResponse, rollenResponse] = await Promise.all([get('/users'), get('/users/rollen')]);
      setUsers(userResponse.data);
      setRollen(rollenResponse.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      openNotificationError('Fout bij ophalen van de data', error)
    }
  };

  const handleSave = async (row) => {
    try {
      await put(`/users/${row._id}`, { naam: row.naam, rol: row.rol });
      setUsers((prev) => prev.map((user) => (user._id === row._id ? row : user)));
    } catch (error) {
      console.error('Error updating user:', error);
      openNotificationError('Fout bij updaten van de user', error)
    }
  };

  const handleDelete = async (key) => {
    try {
      await del(`/users/${key}`);
      openNotificationSuccess("Users succesvol verwijderd")
      await fetchData()
    } catch (error) {
      console.error('Error deleting user:', error);
      openNotificationError('Fout bij het verwijderen van de user', error)
    }
  };

  const handlePaswoordChange = async  (key) => {


    try{
      const response = await get("/users/resetPassword/"+key._id)
      openNotificationSuccess(`paswoord bijgewerkt voor : ${key.naam}  wachtwoord: ${response.data.wachtwoord}`, 0)
     
    }catch(error){
      console.error('Error changing paswoord:', error);
      openNotificationError("Fout bij het veranderen van het wachtwoord", error)
    }
    
  }

  const columns = [
    {
      title: 'Naam',
      dataIndex: 'naam',
      editable: true,
      sorter: (a, b) => a.naam.localeCompare(b.naam),
    },
    {
      title: 'Email',
      dataIndex: 'email',
    },
    {
      title: 'Rol',
      dataIndex: 'rol',
      editable: true,
      inputType: 'select',
      options: rollen, // Assuming this is an array of available roles
      filters: rollen.map((rol) => ({
        text: rol,  // Display text in the filter dropdown
        value: rol, // Value used for filtering
      })),
      onFilter: (value, record) => record.rol === value, // Ensures filtering by role
      sorter: (a, b) => a.rol.localeCompare(b.rol),
    },
    
    {
      title: 'Acties',
      dataIndex: 'operation',
      width: '250px',
      render: (_, record) => (
        <Space size="small">

          <Button
            type="primary"
            icon={<LockOutlined />}
            size="small"
            onClick={() => handlePaswoordChange(record)}
          >
            Wachtwoord resetten
          </Button>
          <Popconfirm
            title="Weet je het zeker?"
            okText="Ja"
            cancelText="Nee"
            onConfirm={() => handleDelete(record._id)}
          >
            <Button 
              icon={<DeleteOutlined />} 
              danger
              size="small"
            >
            </Button>

          </Popconfirm>
      </Space>
      ),
    },
  ];

  const components = {
    body: {
      row: EditableRow,
      cell: EditableCell,
    },
  };

  const columnConfig = columns.map((col) => {
    if (!col.editable) return col;
    return {
      ...col,
      onCell: (record) => ({
        record,
        editable: col.editable,
        dataIndex: col.dataIndex,
        title: col.title,
        handleSave,
        inputType: col.inputType,
        options: col.options,
      }),
    };
  });

  return (
    <div className='Screen-container'>
      <h2 className='title'>Gebruikersbeheer</h2>
      <Table
        components={components}
        rowClassName={() => 'editable-row'}
        bordered
        dataSource={users.map((user) => ({ ...user, key: user._id }))}
        columns={columnConfig}
        pagination={false}
        scroll={{ y: 0.5 * window.innerHeight }}
      />
    </div>
  );
};

export default GebruikersBeheerScreen;
